import React, { useState, useEffect, useMemo } from 'react';
import { useApp } from '../../../../contexts/AppContext';
import { useData } from '../../../../hooks/useData';
import { useErrorLogging, ERROR_SEVERITY, ERROR_CATEGORY } from '../../../../utils/errorLogging';
import { TABLES } from '../../../../constants/supabaseConstants';
import { supabase } from '../../../../config/supabase';
import { createAndTagProjectItem, MASTER_ITEM_IDS } from '../../../../utils/autoProjectItems';
import modalStyles from '../../../../styles/modals.module.css';

// Shading bundle children
const SHADING_OPTIONS = [
  { id: '6d2a28bd-408e-4909-ae1f-af3b3716c0ac', name: 'תריס חשמלי' },
  { id: '279db709-1ca1-4500-bfad-0c8ced20bde0', name: 'צלון' },
  { id: 'ad30c611-a88d-4418-9aae-2e604c69836d', name: 'וילון' },
];

// Window option groups from master_items.item_options
const PROFILE_OPTIONS = ['בלגי', 'אלומיניום', 'עץ'];
const OPENING_OPTIONS = [
  'ציר צד (פתיחה רגילה)',
  'הזזה',
  'דריי קיפ',
  'קיפ',
  'פתיחה כלפי חוץ',
  'הרמה והזזה',
];

const WindowFormModal = ({
  open,
  spaceId,
  space,
  onClose,
  onSubmitRef,
  window: existingWindow,
}) => {
  const { currentProject } = useApp();
  const { logError } = useErrorLogging();
  const isEditMode = !!existingWindow?.id;

  // Extract profile/opening/shading from linked project_items when editing
  const existingOptions = useMemo(() => {
    if (!existingWindow?._linkedItems) return {};
    const items = existingWindow._linkedItems;
    const wi = items?.find(i => i.master_item_id === MASTER_ITEM_IDS.WINDOW);
    const si = items?.find(i => i.master_item_id !== MASTER_ITEM_IDS.WINDOW);
    return {
      profile: wi?.selected_option?.['פרופיל'] || '',
      opening: wi?.selected_option?.['פתיחה'] || '',
      shading: si?.master_item_id || '',
    };
  }, [existingWindow]);

  const [formData, setFormData] = useState({
    window_name: existingWindow?.window_name || '',
    width: existingWindow?.width || '',
    height: existingWindow?.height || '',
    notes: existingWindow?.notes || '',
    profile: existingOptions.profile || '',
    opening: existingOptions.opening || '',
    shading: existingOptions.shading || '',
  });

  // Reset form when modal opens with different window
  useEffect(() => {
    if (open) {
      setFormData({
        window_name: existingWindow?.window_name || '',
        width: existingWindow?.width || '',
        height: existingWindow?.height || '',
        notes: existingWindow?.notes || '',
        profile: existingOptions.profile || '',
        opening: existingOptions.opening || '',
        shading: existingOptions.shading || '',
      });
      setErrors({});
    }
  }, [open, existingWindow?.id]);

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const { insert: insertWindow, update: updateWindow } = useData(TABLES.PROJECT_WINDOWS, {
    enabled: !!currentProject?.id && !!spaceId,
  });

  // Fetch project default options for חלון (set in HomeChoicesSection)
  const { data: projectDefaultOptions } = useData(TABLES.PROJECT_DEFAULT_OPTIONS, {
    select: 'master_item_id, selected_option',
    filter: { project_id: currentProject?.id },
    enabled: !!currentProject?.id,
    realtime: false,
  });

  // Extract default profile/opening from project defaults (new flat format)
  const windowDefaults = useMemo(() => {
    const windowDefault = projectDefaultOptions?.find(
      opt => opt.master_item_id === MASTER_ITEM_IDS.WINDOW,
    );
    const sel = windowDefault?.selected_option;
    if (!sel) return { profile: '', opening: '' };
    // New format stores directly as { "פרופיל": "בלגי", "פתיחה": "הזזה", cost: ..., notes: ... }
    return {
      profile: sel['פרופיל'] || '',
      opening: sel['פתיחה'] || '',
    };
  }, [projectDefaultOptions]);

  // Pre-populate profile/opening from project defaults on open
  useEffect(() => {
    if (open && windowDefaults) {
      setFormData(prev => ({
        ...prev,
        profile: prev.profile || windowDefaults.profile,
        opening: prev.opening || windowDefaults.opening,
      }));
    }
  }, [open, windowDefaults]);

  const validateForm = () => {
    const newErrors = {};

    // Validate width
    const width = Number(formData.width);
    if (!formData.width || isNaN(width)) {
      newErrors.width = 'יש להזין רוחב תקין';
    } else if (width <= 0) {
      newErrors.width = 'רוחב חייב להיות מספר חיובי';
    } else if (width > 1000) {
      newErrors.width = 'רוחב גדול מדי';
    }

    // Validate height
    const height = Number(formData.height);
    if (!formData.height || isNaN(height)) {
      newErrors.height = 'יש להזין גובה תקין';
    } else if (height <= 0) {
      newErrors.height = 'גובה חייב להיות מספר חיובי';
    } else if (height > 1000) {
      newErrors.height = 'גובה גדול מדי';
    }

    // Validate window name if provided
    if (formData.window_name && formData.window_name.length > 100) {
      newErrors.window_name = 'שם החלון ארוך מדי';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generate window name based on space info
  const generateWindowName = () => {
    if (!space || !space.floor) return '';

    // Get floor name
    const floorName =
      space.floor.floor_number === 0
        ? 'קומת-קרקע'
        : space.floor.floor_number < 0
          ? 'מרתף'
          : `קומה-${['ראשונה', 'שנייה', 'שלישית', 'רביעית', 'חמישית', 'שישית', 'שביעית', 'שמינית', 'תשיעית', 'עשירית'][space.floor.floor_number - 1] || space.floor.floor_number}`;

    // Get space name, with fallbacks
    const spaceName = space.custom_space_name || space.master_space?.name || 'ללא-שם';
    const prefix = `${floorName}-${spaceName}-חלון`;

    // Find next available number from existing windows
    const existingNumbers = (space.windows || [])
      .map(w => w.window_name)
      .filter(Boolean)
      .filter(name => name.startsWith(prefix))
      .map(name => parseInt(name.replace(prefix, '')))
      .filter(num => !isNaN(num));

    const nextNumber = existingNumbers.length > 0 ? Math.max(...existingNumbers) + 1 : 1;
    return `${prefix}${nextNumber}`;
  };

  const handleSubmit = async e => {
    if (e) e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      const windowData = {
        window_name: formData.window_name || generateWindowName(),
        width: Number(formData.width),
        height: Number(formData.height),
        notes: formData.notes,
      };

      if (isEditMode) {
        // ─── Update existing window ────────────────────────────────
        await updateWindow({ id: existingWindow.id, data: windowData });

        // Update the linked project_item's selected_option (profile/opening)
        const selectedOption = {};
        if (formData.profile) selectedOption['פרופיל'] = formData.profile;
        if (formData.opening) selectedOption['פתיחה'] = formData.opening;

        const linkedWindowItem = existingWindow._linkedItems?.find(
          i => i.master_item_id === MASTER_ITEM_IDS.WINDOW,
        );
        if (linkedWindowItem) {
          await supabase
            .from('project_items')
            .update({
              selected_option: Object.keys(selectedOption).length > 0 ? selectedOption : null,
            })
            .eq('id', linkedWindowItem.id);
        }

        // Handle shading change: delete old shading item, create new if needed
        const oldShadingItem = existingWindow._linkedItems?.find(
          i => i.master_item_id !== MASTER_ITEM_IDS.WINDOW,
        );
        if (oldShadingItem && oldShadingItem.master_item_id !== formData.shading) {
          await supabase
            .from('project_items')
            .update({ deleted_at: new Date().toISOString() })
            .eq('id', oldShadingItem.id);
        }
        if (formData.shading && formData.shading !== oldShadingItem?.master_item_id) {
          try {
            await createAndTagProjectItem(
              supabase,
              { projectId: currentProject.id, masterItemId: formData.shading, spaceId },
              'window_id',
              existingWindow.id,
            );
          } catch (e) {
            console.error('Error creating shading item:', e);
          }
        }
      } else {
        // ─── Create new window ─────────────────────────────────────
        windowData.space_id = spaceId;
        windowData.window_type = null;
        windowData.sill_height = null;

        const windowResult = await insertWindow(windowData);
        const windowId = windowResult?.[0]?.id;

        if (windowId) {
          try {
            const selectedOption = {};
            if (formData.profile) selectedOption['פרופיל'] = formData.profile;
            if (formData.opening) selectedOption['פתיחה'] = formData.opening;

            await createAndTagProjectItem(
              supabase,
              {
                projectId: currentProject.id,
                masterItemId: MASTER_ITEM_IDS.WINDOW,
                spaceId,
                selectedOption: Object.keys(selectedOption).length > 0 ? selectedOption : null,
              },
              'window_id',
              windowId,
            );
          } catch (itemError) {
            console.error('Error creating window project item:', itemError);
          }

          if (formData.shading) {
            try {
              await createAndTagProjectItem(
                supabase,
                { projectId: currentProject.id, masterItemId: formData.shading, spaceId },
                'window_id',
                windowId,
              );
            } catch (shadingError) {
              console.error('Error creating shading project item:', shadingError);
            }
          }
        }
      }

      onClose();
    } catch (error) {
      logError({
        error,
        severity: ERROR_SEVERITY.ERROR,
        category: ERROR_CATEGORY.DATA,
        message: `Failed to ${isEditMode ? 'update' : 'create'} window`,
        context: { spaceId, projectId: currentProject.id },
      });

      setErrors(prev => ({
        ...prev,
        submit: 'שגיאה בשמירת החלון. אנא נסה שוב.',
      }));
    } finally {
      setIsLoading(false);
    }
  };

  // Register submit handler with ModalManager
  useEffect(() => {
    if (open && onSubmitRef) {
      onSubmitRef(() => handleSubmit);
    }
  }, [open, onSubmitRef, handleSubmit]);

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
    // Clear error when field is modified
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  if (!open) return null;

  return (
    <div className={modalStyles.overlay} onClick={onClose}>
      <div className={modalStyles.modalSmall} dir="rtl" onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div className={modalStyles.header}>
          <h2 className={modalStyles.headerTitle}>{isEditMode ? 'עריכת חלון' : 'הוסף חלון חדש'}</h2>
        </div>

        {/* Content */}
        <div className={modalStyles.content}>
          {errors.submit && <div className={modalStyles.error}>{errors.submit}</div>}

          <div className={modalStyles.formGrid}>
            {/* Window Name - Optional */}
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                שם חלון
                <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
              </label>
              <input
                type="text"
                className={modalStyles.fieldInput}
                name="window_name"
                value={formData.window_name}
                onChange={handleChange}
                placeholder={generateWindowName()}
              />
            </div>

            {/* Width and Height - Required */}
            <div className={modalStyles.formRow}>
              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>
                  <span className={modalStyles.fieldRequired}>*</span>
                  רוחב (ס&quot;מ)
                </label>
                <input
                  type="number"
                  className={`${modalStyles.fieldInput} ${errors.width ? modalStyles.error : ''}`}
                  name="width"
                  value={formData.width}
                  onChange={handleChange}
                  min="0"
                  step="1"
                  placeholder="120"
                />
                {errors.width && <div className={modalStyles.fieldError}>{errors.width}</div>}
              </div>

              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>
                  <span className={modalStyles.fieldRequired}>*</span>
                  גובה (ס&quot;מ)
                </label>
                <input
                  type="number"
                  className={`${modalStyles.fieldInput} ${errors.height ? modalStyles.error : ''}`}
                  name="height"
                  value={formData.height}
                  onChange={handleChange}
                  min="0"
                  step="1"
                  placeholder="150"
                />
                {errors.height && <div className={modalStyles.fieldError}>{errors.height}</div>}
              </div>
            </div>

            {/* Profile - Optional */}
            <div className={modalStyles.formRow}>
              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>
                  פרופיל
                  <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
                </label>
                <select
                  className={modalStyles.fieldSelect}
                  name="profile"
                  value={formData.profile}
                  onChange={handleChange}
                  disabled={isLoading}
                >
                  <option value="">בחר פרופיל</option>
                  {PROFILE_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>

              {/* Opening - Optional */}
              <div className={modalStyles.field}>
                <label className={modalStyles.fieldLabel}>
                  סוג פתיחה
                  <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
                </label>
                <select
                  className={modalStyles.fieldSelect}
                  name="opening"
                  value={formData.opening}
                  onChange={handleChange}
                  disabled={isLoading}
                >
                  <option value="">בחר סוג פתיחה</option>
                  {OPENING_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Shading - Optional */}
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                פתרון הצללה
                <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
              </label>
              <select
                className={modalStyles.fieldSelect}
                name="shading"
                value={formData.shading}
                onChange={handleChange}
                disabled={isLoading}
              >
                <option value="">ללא</option>
                {SHADING_OPTIONS.map(opt => (
                  <option key={opt.id} value={opt.id}>
                    {opt.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Notes - Optional */}
            <div className={modalStyles.field}>
              <label className={modalStyles.fieldLabel}>
                הערות
                <span className={modalStyles.fieldOptional}>(אופציונלי)</span>
              </label>
              <input
                type="text"
                className={modalStyles.fieldInput}
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                disabled={isLoading}
                placeholder="הערות נוספות..."
              />
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className={modalStyles.footer}>
          <button
            type="button"
            className={modalStyles.cancelButton}
            onClick={onClose}
            disabled={isLoading}
          >
            ביטול
          </button>
          <button
            type="button"
            className={modalStyles.primaryButton}
            onClick={handleSubmit}
            disabled={isLoading}
          >
            {isLoading ? 'שומר...' : 'שמור'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default WindowFormModal;
